import clsx from 'clsx'
import type { ReactNode } from 'react'

import type { FullShipment } from '@fv/client-types'

import InfoBox from '@/components/InfoBox'
import { Loading } from '@/components/Loading'
import {
  SliderContentBody,
  SliderContentFooter,
} from '@/features/shipment-details-panel/SliderContentLayout'

import { BookTpeButton } from './BookTpeButton'
import type { RouteGuideView } from './hooks'
import { RouteGuideEmptyState } from './RouteGuideEmptyState'
import { RouteGuideProgress, type RouteGuideStep } from './RouteGuideProgress'
import { SkipToSpotButton } from './SkipToSpotButton'

type Props = {
  load: FullShipment
  view: RouteGuideView
  header?: ReactNode
  footerActions?: ReactNode
  selectedCarrierId?: string
  onStepClick?: (step: RouteGuideStep) => void
  onSkipped?: () => void
}

export const RouteGuideOverview = ({
  load,
  view,
  header,
  footerActions,
  selectedCarrierId,
  onStepClick,
  onSkipped,
}: Props) => {
  const {
    execution,
    isLoading,
    isError,
    canSkip,
    skipToSpot,
    tpeRate,
    bookTpeRate,
  } = view

  return (
    <>
      <SliderContentBody>
        {header}
        {isLoading ? (
          <Loading className="mb-0" />
        ) : isError ? (
          <InfoBox icon="exclamation-triangle" className="mb-0">
            Unable to load the route guide for this shipment.
          </InfoBox>
        ) : execution ? (
          <RouteGuideProgress
            execution={execution}
            actions={
              <BookTpeButton bookTpeRate={bookTpeRate} tpeRate={tpeRate} />
            }
            selectedCarrierId={selectedCarrierId}
            onStepClick={onStepClick}
          />
        ) : (
          <RouteGuideEmptyState
            status={load.routeGuide?.status}
            className="mb-0"
          />
        )}
      </SliderContentBody>
      {(canSkip || footerActions) && (
        <SliderContentFooter
          className={clsx(!footerActions && 'justify-center')}
        >
          {canSkip && (
            <SkipToSpotButton skipToSpot={skipToSpot} onSkipped={onSkipped} />
          )}
          {footerActions}
        </SliderContentFooter>
      )}
    </>
  )
}
